import { AnalyticsContext } from "@/providers/AnalyticsProvider";
import { useContext } from "react";
import { UserRole, useUserSettings } from "./useUserSettings";

export type EventType =
  // Overview
  | "Overview Viewed"

  // Purchase orders
  | "Purchase Orders Viewed"
  | "Purchase Orders Uploaded"
  | "Purchase Orders Deleted"
  | "Purchase Orders Template Downloaded"
  | "Purchase Orders Exported"

  // Consumables
  | "Consumable Viewed"
  | "Consumables Viewed"
  | "Consumable Selected"
  | "Consumable Unit Updated"

  // Goods and services
  | "Goods & Services Viewed"
  | "Goods & Services Item Viewed"
  | "Goods & Services Item Selected"
  | "Goods & Services Item Unit Updated"

  // Sales orders
  | "Sales Orders Viewed"
  | "Sales Orders Uploaded"
  | "Sales Orders Deleted"
  | "Sales Orders Template Downloaded"
  | "Sales Orders Exported"

  // Products
  | "Products Viewed"
  | "Products Deleted"
  | "Products Exported"
  | "Products Uploaded"
  | "Products Changed Scope"
  | "Product Viewed"
  | "Product Deleted"
  | "Product Selected"
  | "Product Exported"
  | "Product Changed Scope"
  | "Product Template Downloaded"
  | "Product Details Viewed"
  | "Product Details Updated"
  | "Product Uploaded"
  | "BOM Viewed"
  | "BOM Deleted"
  | "Bill of Materials Overview Viewed"

  // Packaging
  | "Packaging Overview Viewed"
  | "Packagings Deleted"
  | "Packagings Exported"
  | "Packagings Uploaded"
  | "Packaging Viewed"
  | "Packaging Deleted"
  | "Packaging Selected"
  | "Packaging Exported"
  | "Packaging Template Downloaded"

  // Materials
  | "Materials Viewed"
  | "Materials Deleted"
  | "Material Viewed"
  | "Material Selected"
  | "Material Deleted"
  | "Impact Reference Updated"
  | "Impact Reference Created"
  | "Impact Reference Deleted"
  | "Material Unit Updated"

  // Facilities
  | "Facilities Viewed"
  | "Facility Viewed"
  | "Facility Added"
  | "Facility Changed Scope"
  | "Facility Changed Owned"
  | "Facility Address Validation Failed"
  | "Contract Added"
  | "Contract Updated"
  | "Contract Deleted"
  | "Facility Deleted"
  | "Facilities Deleted"

  // Inbound Transport
  | "Inbound Transport Viewed"
  | "Inbound Transport Data Deleted"
  | "Inbound Transport Data Uploaded"
  | "Inbound Transport Template Downloaded"
  | "Inbound Transport Exported"
  | "Inbound Transport Route Viewed"
  | "Inbound Transport Route Uploaded"
  | "Inbound Transport Route Deleted"

  // Outbound transport
  | "Outbound Transport Viewed"
  | "Outbound Transport Data Deleted"
  | "Outbound Transport Data Uploaded"
  | "Outbound Transport Template Downloaded"
  | "Outbound Transport Exported"
  | "Outbound Transport Route Viewed"
  | "Outbound Transport Route Data Uploaded"
  | "Outbound Transport Route Data Removed"
  | "Outbound Transport Route Deleted"

  // Dashboards
  | "Company Dashboard Environmental Viewed"
  | "Material Dashboard Environmental Viewed"
  | "Facility Dashboard Environmental Viewed"
  | "Transport Dashboard Environmental Viewed"
  | "Product Dashboard Environmental Viewed"
  | "Company Dashboard Carbon Viewed"
  | "Material Dashboard Carbon Viewed"
  | "Facility Dashboard Carbon Viewed"
  | "Transport Dashboard Carbon Viewed"
  | "Product Dashboard Carbon Viewed"
  | "Dashboard Date Range Changed"
  | "Customer Dashboard Carbon Viewed"
  | "Customer Dashboard Environmental Viewed"
  | "Employee Dashboard Carbon Viewed"
  | "Employee Dashboard Environmental Viewed"

  // Management
  | "Manage settings viewed"
  | "Account viewed"
  | "Team viewed"

  // Proxies
  | "Proxy Created"
  | "Proxy Updated"
  | "Proxies Created"
  | "Proxies Updated"
  | "Proxy Deleted"
  | "Proxies Deleted"

  // Impact references
  | "All References Overview Viewed"
  | "EcoInvent References Overview Viewed"
  | "Custom References Overview Viewed"
  | "EPD References Overview Viewed"
  | "Agri-footprint References Overview Viewed"
  | "Root References Overview Viewed"
  | "EPD Viewed"
  | "EPD Updated"
  | "EPD Created"
  | "EPD Deleted"
  | "Custom Impact Reference Viewed"
  | "Custom Impact Reference Updated"
  | "Custom Impact Reference Created"
  | "Custom Impact Reference Deleted"
  | "Root References Overview Viewed"
  | "Root Impact Reference Viewed"
  | "Root Impact Reference Updated"
  | "Root Impact Reference Created"
  | "Root Impact Reference Deleted"

  // Residual waste
  | "Residual Waste Added"
  | "Residual Waste Updated"

  // User management
  | "New user invited"

  // EOL
  | "EOL viewed"
  | "EOL reference deleted"
  | "EOL reference edited"

  // Capital Goods
  | "Capital Goods Added"
  | "Capital Goods Updated"

  // Usage
  | "Usage Overview Viewed"
  | "Usage Modal Opened"
  | "Usage Viewed In Product Detail Page"
  | "Usage Submitted From Modal"

  // Packaging Material
  | "Packaging Materials Viewed"
  | "Packaging Materials Deleted"
  | "Packaging Material Viewed"
  | "Packaging Material Reference Updated"
  | "Packaging Material Unit Updated"
  | "Packaging Material Reference Deleted"
  | "Packaging Material Deleted"
  | "Packaging Material Selected"

  // General
  | "Logged Out";

export const trackedRoles: UserRole[] = ["company_admin", "user"];

export default function useAnalytics({ enabled }: { enabled?: boolean } = { enabled: true }) {
  const client = useContext(AnalyticsContext);
  const userSettings = useUserSettings();
  const role = userSettings.data?.role;

  const baseParams = {
    "Company ID": enabled ? userSettings.data?.company_id : "external",
    Pathname: window.location.pathname,
  };

  const track = (event: EventType, params?: Record<string, any>) => {
    if (role && trackedRoles.includes(role)) {
      return client.capture(event, { ...baseParams, params });
    }
  };

  const identify = (companyId: number, companyName: string, userId: number) => {
    client.identify("ROOT_ID_" + String(userId), {
      "Company Name": companyName,
      "Company ID": companyId,
    });
  };

  return { client, track, identify };
}
